import React, { useState } from "react";

import { Button, Dialog, DialogContent, DialogTitle, Grid } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";

import { moveShipmentDetailToOOS, shipShipmentDetail } from "api/shipment";

import { ConfirmDialog } from "components/Custom";

import { popupNotification, useLayoutDispatch } from "context/LayoutContext";

const SHIPPED_STATUSES = ["Shipped", "Invoiced"];
const CLOSED_STATUSES = [
  "Shipped",
  "Invoiced",
  "Cancelled",
  "Pending_Cancellation",
  "Out_of_Stock",
];

// Ship works from any live status, picked or not, in stock or not. Out of Stock is excluded with
// the finished statuses: a backordered line has no label until reprocess gives it one.
const SHIPPABLE_STATUSES = [
  "Unassigned",
  "Assigned",
  "Picking",
  "Picked",
  "Packed",
];

// A partly shipped line keeps status Picked, so its transactions are the only honest signal.
export const hasShippedUnits = (item) =>
  SHIPPED_STATUSES.includes(item.status) ||
  (item.transactions || []).some(
    (t) => t.action_type === "Shipped" && !t.is_cancelled,
  );

/**
 * One "Actions" button per line, opening a modal with whatever that line actually allows.
 * The row used to carry a stack of buttons; with ship and out-of-stock added that turned the
 * table into a toolbar with a shipment attached.
 */
export default function ShipmentDetailActions({
  item,
  canUpdate,
  canCancel,
  canShip,
  onWrongClick,
  onCancelClick,
  onTimelineClick,
  onDone,
}) {
  const theme = useTheme();
  const layoutDispatch = useLayoutDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [confirming, setConfirming] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const notify = (message, status) =>
    popupNotification({ dispatch: layoutDispatch, message, status });

  const handleResponse = (res, successMessage) => {
    setIsLoading(false);
    if (!res?.success) {
      return notify(res?.message || "Action failed.", "error");
    }
    setConfirming("");
    setIsOpen(false);
    notify(res?.message || successMessage, "success");
    onDone();
  };

  const handleShip = () => {
    setIsLoading(true);
    shipShipmentDetail({
      body: { detail_id: item.id },
      responseSetter: (res) => handleResponse(res, "Line shipped."),
    });
  };

  const handleMoveToOOS = () => {
    setIsLoading(true);
    moveShipmentDetailToOOS({
      body: { detail_id: item.id },
      responseSetter: (res) => handleResponse(res, "Line moved to Out of Stock."),
    });
  };

  const run = (handler) => () => {
    setIsOpen(false);
    handler(item);
  };

  const actions = [
    {
      key: "wrong",
      label: item.is_wrong_shipped ? "Update Wrong" : "Mark As Wrong",
      color: theme.palette.colors.slate500,
      show: SHIPPED_STATUSES.includes(item.status) && canUpdate,
      onClick: run(onWrongClick),
    },
    {
      key: "ship",
      label: "Ship",
      color: theme.palette.colors.emerald700,
      show: SHIPPABLE_STATUSES.includes(item.status) && canShip,
      onClick: () => setConfirming("ship"),
    },
    {
      key: "oos",
      label: "Move to OOS",
      color: theme.palette.colors.amber600,
      show: !CLOSED_STATUSES.includes(item.status) && !hasShippedUnits(item) && canCancel,
      onClick: () => setConfirming("oos"),
    },
    {
      key: "cancel",
      label: "Cancel",
      color: theme.palette.error.main,
      show: !["Cancelled", "Pending_Cancellation"].includes(item.status) && canCancel,
      onClick: run(onCancelClick),
    },
    {
      key: "timeline",
      label: "Detail",
      color: theme.palette.secondary.main,
      show: true,
      onClick: run(onTimelineClick),
    },
  ].filter((action) => action.show);

  return (
    <>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={() => setIsOpen(true)}
      >
        Actions
      </Button>
      <Dialog open={isOpen} maxWidth="xs" fullWidth onClose={() => setIsOpen(false)}>
        <DialogTitle>{`${item.product?.sku} — ${item.status?.split("_").join(" ")}`}</DialogTitle>
        <DialogContent style={{ paddingBottom: "1.5rem" }}>
          <Grid container spacing={1}>
            {actions.map((action) => (
              <Grid item lg={12} xs={12} key={action.key}>
                <Button
                  fullWidth
                  variant="contained"
                  style={{
                    backgroundColor: action.color,
                    color: theme.palette.colors.white,
                    textTransform: "none",
                  }}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
      {confirming === "ship" ? (
        <ConfirmDialog
          open={true}
          title="Ship Item"
          confirmText={isLoading ? "Shipping..." : "Confirm"}
          onClose={() => setConfirming("")}
          onConfirm={handleShip}
        >
          {["Picked", "Packed"].includes(item.status)
            ? `${item.product?.sku} will be shipped and its picked stock removed from the buggy. The shipment closes out if this is its last open line.`
            : `${item.product?.sku} was never picked, so it will be shipped as is. Whatever the pick location still holds is deducted; anything missing stays as a stock discrepancy.`}
        </ConfirmDialog>
      ) : null}
      {confirming === "oos" ? (
        <ConfirmDialog
          open={true}
          title="Move to Out of Stock"
          confirmText={isLoading ? "Moving..." : "Confirm"}
          onClose={() => setConfirming("")}
          onConfirm={handleMoveToOOS}
        >
          {`${item.product?.sku} moves to the backorder shipment as Out of Stock. Its picked stock returns to the pick location and its tracking number is cleared. The carrier label is only cancelled when the whole shipment moves.`}
        </ConfirmDialog>
      ) : null}
    </>
  );
}
